/**
 * Kawaii Stop native navigation controller.
 *
 * Umino still owns the outer mobile drawer. This controller owns only the
 * Kawaii Stop parent/category panels and the centered desktop mega menu.
 */
(() => {
  'use strict';

  const GLOBAL_KEY = '__ksNativeMenuV240';
  const DESKTOP = window.matchMedia('(min-width: 1025px)');
  const HOVER_DESKTOP = window.matchMedia(
    '(min-width: 1025px) and (hover: hover) and (pointer: fine)',
  );
  const NAV_SELECTOR = '[data-ks-menu]';
  const PARENT_SELECTOR = '[data-ks-menu-parent]';
  const TRIGGER_SELECTOR = '[data-ks-menu-trigger]';
  const PANEL_SELECTOR = '[data-ks-menu-panel]';
  const CATEGORY_TRIGGER_SELECTOR = '[data-ks-category-trigger]';
  const CATEGORY_PANEL_SELECTOR = '[data-ks-category-panel]';
  const BACKDROP_CLASS = 'ks-menu-backdrop';
  const OPEN_ROOT_CLASS = 'ks-menu-open';
  const DESKTOP_CLOSE_DELAY = 180;

  if (window[GLOBAL_KEY]) {
    window[GLOBAL_KEY].init(document);
    return;
  }

  let backdrop = null;
  let activeDesktopParent = null;
  let desktopCloseTimer = 0;

  const cancelDesktopClose = () => {
    if (!desktopCloseTimer) return;
    window.clearTimeout(desktopCloseTimer);
    desktopCloseTimer = 0;
  };

  const scheduleDesktopClose = (scope = document) => {
    cancelDesktopClose();
    desktopCloseTimer = window.setTimeout(() => {
      desktopCloseTimer = 0;
      closeDesktopMenus(scope);
    }, DESKTOP_CLOSE_DELAY);
  };

  const directTrigger = (parent) =>
    parent?.querySelector(`:scope > ${TRIGGER_SELECTOR}`) || null;

  const directPanel = (parent) =>
    parent?.querySelector(`:scope > ${PANEL_SELECTOR}`) || null;

  const controlledElement = (control) => {
    const id = control?.getAttribute('aria-controls');
    return id ? document.getElementById(id) : null;
  };

  const safeFocus = (element) => {
    if (!(element instanceof HTMLElement)) return;
    window.setTimeout(() => element.focus({ preventScroll: true }), 0);
  };

  const setBackdropOpen = (open) => {
    document.documentElement.classList.toggle(OPEN_ROOT_CLASS, open);
    if (!backdrop) return;

    backdrop.classList.toggle('is-active', open);
    backdrop.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const ensureBackdrop = () => {
    backdrop = document.querySelector(`.${BACKDROP_CLASS}`);
    if (!backdrop) {
      backdrop = document.createElement('button');
      backdrop.type = 'button';
      backdrop.tabIndex = -1;
      backdrop.className = BACKDROP_CLASS;
      backdrop.setAttribute('aria-label', 'Close navigation menu');
      backdrop.addEventListener('click', () => closeDesktopMenus());
      document.body.append(backdrop);
    }

    backdrop.setAttribute('aria-hidden', 'true');
    return backdrop;
  };

  const visibleRect = (element) => {
    if (!(element instanceof HTMLElement)) return null;

    const styles = window.getComputedStyle(element);
    if (
      styles.display === 'none' ||
      styles.visibility === 'hidden' ||
      Number.parseFloat(styles.opacity || '1') === 0
    ) {
      return null;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0 || rect.bottom <= 0) return null;

    return rect;
  };

  const panelAnchorBottom = (nav) => {
    const navRect = visibleRect(nav);
    let bottom = navRect?.bottom || 0;

    const pageHeader =
      nav.closest('#page-header') || document.querySelector('#page-header');

    const candidates = [
      nav.closest('#sticky-header'),
      nav.closest('[data-sticky-header]'),
      nav.closest('.sticky-header'),
      nav.closest('.header-sticky'),
      pageHeader?.querySelector('#sticky-header'),
      pageHeader?.querySelector('[data-sticky-header]'),
      pageHeader?.querySelector('.sticky-header'),
      pageHeader?.querySelector('.header-sticky'),
      pageHeader?.querySelector('.header-container'),
      pageHeader,
    ];

    const seen = new Set();

    candidates.forEach((candidate) => {
      if (!(candidate instanceof HTMLElement) || seen.has(candidate)) return;
      seen.add(candidate);

      const rect = visibleRect(candidate);
      if (!rect) return;

      /*
       * Only accept header candidates that occupy the top portion of the
       * viewport and finish near the visible navigation. This prevents an
       * off-screen outer header wrapper from anchoring the menu at 0px after
       * the sticky header takes over.
       */
      const nearViewportTop = rect.top < Math.max(220, rect.height * 2);
      const nearNavigation =
        !navRect || rect.bottom <= navRect.bottom + Math.max(96, rect.height);

      if (nearViewportTop && nearNavigation) {
        bottom = Math.max(bottom, rect.bottom);
      }
    });

    return Math.max(
      0,
      Math.min(Math.round(bottom), Math.max(0, window.innerHeight - 120)),
    );
  };

  const updatePanelTop = (nav) => {
    if (!(nav instanceof HTMLElement)) return;

    const bottom = panelAnchorBottom(nav);
    nav.style.setProperty('--ks-menu-panel-top', `${bottom}px`);
  };

  let panelPositionFrame = 0;

  const requestPanelTopUpdate = () => {
    if (
      panelPositionFrame ||
      !DESKTOP.matches ||
      !(activeDesktopParent instanceof HTMLElement)
    ) {
      return;
    }

    const nav = activeDesktopParent.closest(NAV_SELECTOR);
    if (!nav) return;

    panelPositionFrame = window.requestAnimationFrame(() => {
      panelPositionFrame = 0;
      updatePanelTop(nav);
    });
  };

  const setDesktopParentState = (parent, open) => {
    if (!(parent instanceof HTMLElement)) return;

    const trigger = directTrigger(parent);
    const panel = directPanel(parent);
    parent.classList.toggle('visible', open);
    parent.classList.toggle('is-ks-desktop-open', open);
    parent.classList.toggle('bls-item-active-submenu', open);
    parent.isActive = open;
    trigger?.setAttribute('aria-expanded', open ? 'true' : 'false');
    panel?.setAttribute('aria-hidden', open ? 'false' : 'true');
  };

  const activateCategory = (trigger, openMobilePanel = false) => {
    if (!(trigger instanceof HTMLElement)) return;

    const menuPanel = trigger.closest(PANEL_SELECTOR);
    const categoryPanel = controlledElement(trigger);
    if (!menuPanel || !categoryPanel) return;

    menuPanel.querySelectorAll(CATEGORY_TRIGGER_SELECTOR).forEach((item) => {
      const selected = item === trigger;
      item.classList.toggle('active', selected);
      item.setAttribute('aria-selected', selected ? 'true' : 'false');
      item.tabIndex = selected ? 0 : -1;
    });

    menuPanel.querySelectorAll(CATEGORY_PANEL_SELECTOR).forEach((item) => {
      const selected = item === categoryPanel;
      item.classList.toggle('active', selected);
      item.classList.toggle('is-ks-open', selected && openMobilePanel);
      item.setAttribute(
        'aria-hidden',
        selected && (DESKTOP.matches || openMobilePanel) ? 'false' : 'true',
      );
    });
  };

  const normalizeDesktopCategory = (parent) => {
    const panel = directPanel(parent);
    if (!panel) return;

    const selected =
      panel.querySelector(
        `${CATEGORY_TRIGGER_SELECTOR}[aria-selected="true"]`,
      ) || panel.querySelector(CATEGORY_TRIGGER_SELECTOR);
    if (selected) activateCategory(selected, false);
  };

  const closeDesktopMenus = (scope = document) => {
    cancelDesktopClose();

    if (panelPositionFrame) {
      window.cancelAnimationFrame(panelPositionFrame);
      panelPositionFrame = 0;
    }

    scope.querySelectorAll?.(`${NAV_SELECTOR} ${PARENT_SELECTOR}`).forEach(
      (parent) => setDesktopParentState(parent, false),
    );
    activeDesktopParent = null;
    setBackdropOpen(false);
  };

  const openDesktopMenu = (nav, parent) => {
    cancelDesktopClose();
    updatePanelTop(nav);

    nav.querySelectorAll(PARENT_SELECTOR).forEach((item) => {
      setDesktopParentState(item, item === parent);
    });

    normalizeDesktopCategory(parent);
    activeDesktopParent = parent;
    setBackdropOpen(true);

    /*
     * Re-measure after the open-state classes are applied. Umino can change
     * the sticky header's height or transform during the same frame.
     */
    requestPanelTopUpdate();
    window.setTimeout(() => {
      if (activeDesktopParent === parent) updatePanelTop(nav);
    }, 220);
  };

  const resetMobile = (scope = document) => {
    scope.querySelectorAll?.(`${NAV_SELECTOR} ${PANEL_SELECTOR}`).forEach(
      (panel) => {
        panel.classList.remove('is-ks-open');
        panel.setAttribute('aria-hidden', 'true');
      },
    );
    scope
      .querySelectorAll?.(`${NAV_SELECTOR} ${CATEGORY_PANEL_SELECTOR}`)
      .forEach((panel) => {
        panel.classList.remove('is-ks-open');
        panel.setAttribute('aria-hidden', 'true');
      });
    scope.querySelectorAll?.(`${NAV_SELECTOR} ${PARENT_SELECTOR}`).forEach(
      (parent) => {
        parent.classList.remove('is-ks-desktop-open');
        parent.classList.remove('is-ks-mobile-open');
        directTrigger(parent)?.setAttribute('aria-expanded', 'false');
      },
    );
  };

  const openMobileParent = (nav, parent) => {
    resetMobile(nav);

    const trigger = directTrigger(parent);
    const panel = directPanel(parent);
    if (!trigger || !panel) return;

    parent.classList.add('is-ks-mobile-open');
    trigger.setAttribute('aria-expanded', 'true');
    panel.classList.add('is-ks-open');
    panel.setAttribute('aria-hidden', 'false');

    const selected =
      panel.querySelector(
        `${CATEGORY_TRIGGER_SELECTOR}[aria-selected="true"]`,
      ) || panel.querySelector(CATEGORY_TRIGGER_SELECTOR);
    if (selected) activateCategory(selected, false);

    safeFocus(panel.querySelector('[data-ks-menu-back]'));
  };

  const closeMobileParent = (panel) => {
    if (!(panel instanceof HTMLElement)) return;

    const nav = panel.closest(NAV_SELECTOR);
    const parent = panel.closest(PARENT_SELECTOR);
    const trigger = directTrigger(parent);
    panel.querySelectorAll(CATEGORY_PANEL_SELECTOR).forEach((item) => {
      item.classList.remove('is-ks-open');
      item.setAttribute('aria-hidden', 'true');
    });
    panel.classList.remove('is-ks-open');
    panel.setAttribute('aria-hidden', 'true');
    parent?.classList.remove('is-ks-mobile-open');
    trigger?.setAttribute('aria-expanded', 'false');
    if (nav) safeFocus(trigger);
  };

  const openMobileCategory = (trigger) => {
    activateCategory(trigger, true);
    safeFocus(controlledElement(trigger)?.querySelector('[data-ks-category-back]'));
  };

  const closeMobileCategory = (panel) => {
    if (!(panel instanceof HTMLElement)) return;

    panel.classList.remove('is-ks-open');
    panel.setAttribute('aria-hidden', 'true');
    const id = panel.id;
    const menuPanel = panel.closest(PANEL_SELECTOR);
    const trigger = Array.from(
      menuPanel?.querySelectorAll(CATEGORY_TRIGGER_SELECTOR) || [],
    ).find((item) => item.getAttribute('aria-controls') === id);
    safeFocus(trigger);
  };

  const closeOuterMobileMenu = (nav) => {
    resetMobile(nav);
    document.documentElement.classList.remove('nav-open');
    document.documentElement.classList.remove(OPEN_ROOT_CLASS);
    const toggle = document.querySelector(
      '[aria-controls="KawaiiStopPrimaryNavigation"], .nav-toggle',
    );
    safeFocus(toggle);
  };

  const moveCategoryFocus = (trigger, direction) => {
    const tablist = trigger.closest('[role="tablist"]');
    const tabs = Array.from(
      tablist?.querySelectorAll(CATEGORY_TRIGGER_SELECTOR) || [],
    );
    if (tabs.length < 2) return;

    const current = tabs.indexOf(trigger);
    let next = current;
    if (direction === 'first') next = 0;
    else if (direction === 'last') next = tabs.length - 1;
    else next = (current + direction + tabs.length) % tabs.length;

    tabs[next].focus({ preventScroll: true });
  };

  const handleEscape = (nav) => {
    if (DESKTOP.matches) {
      const trigger = activeDesktopParent
        ? directTrigger(activeDesktopParent)
        : null;
      closeDesktopMenus(nav);
      safeFocus(trigger);
      return;
    }

    const categoryPanel = nav.querySelector(
      `${CATEGORY_PANEL_SELECTOR}.is-ks-open`,
    );
    if (categoryPanel) {
      closeMobileCategory(categoryPanel);
      return;
    }

    const menuPanel = nav.querySelector(`${PANEL_SELECTOR}.is-ks-open`);
    if (menuPanel) {
      closeMobileParent(menuPanel);
      return;
    }

    closeOuterMobileMenu(nav);
  };

  const initNav = (nav) => {
    if (!(nav instanceof HTMLElement) || nav.dataset.ksMenuReady === 'true') {
      return;
    }

    nav.dataset.ksMenuReady = 'true';
    ensureBackdrop();

    nav.querySelectorAll(PARENT_SELECTOR).forEach((parent) => {
      const trigger = directTrigger(parent);
      const panel = directPanel(parent);
      if (!trigger || !panel) return;

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');

      parent.addEventListener('pointerenter', (event) => {
        if (!HOVER_DESKTOP.matches || event.pointerType === 'touch') return;
        cancelDesktopClose();
        openDesktopMenu(nav, parent);
      });

      parent.addEventListener('pointerleave', (event) => {
        if (!HOVER_DESKTOP.matches || event.pointerType === 'touch') return;
        scheduleDesktopClose(nav);
      });

      panel.addEventListener('pointerenter', (event) => {
        if (!HOVER_DESKTOP.matches || event.pointerType === 'touch') return;
        cancelDesktopClose();
      });

      panel.addEventListener('pointerleave', (event) => {
        if (!HOVER_DESKTOP.matches || event.pointerType === 'touch') return;
        scheduleDesktopClose(nav);
      });
    });

    nav.querySelectorAll(CATEGORY_TRIGGER_SELECTOR).forEach((trigger) => {
      trigger.addEventListener('pointerenter', (event) => {
        if (!HOVER_DESKTOP.matches || event.pointerType === 'touch') return;
        cancelDesktopClose();
        activateCategory(trigger, false);
      });
    });

    nav.addEventListener(
      'click',
      (event) => {
        const menuTrigger = event.target.closest(TRIGGER_SELECTOR);
        if (menuTrigger && nav.contains(menuTrigger)) {
          const parent = menuTrigger.closest(PARENT_SELECTOR);
          if (!parent) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          if (DESKTOP.matches) {
            if (activeDesktopParent === parent) closeDesktopMenus(nav);
            else openDesktopMenu(nav, parent);
          } else {
            openMobileParent(nav, parent);
          }
          return;
        }

        const menuBack = event.target.closest('[data-ks-menu-back]');
        if (menuBack && nav.contains(menuBack)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          closeMobileParent(menuBack.closest(PANEL_SELECTOR));
          return;
        }

        const categoryTrigger = event.target.closest(
          CATEGORY_TRIGGER_SELECTOR,
        );
        if (categoryTrigger && nav.contains(categoryTrigger)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          if (DESKTOP.matches) activateCategory(categoryTrigger, false);
          else openMobileCategory(categoryTrigger);
          return;
        }

        const categoryBack = event.target.closest('[data-ks-category-back]');
        if (categoryBack && nav.contains(categoryBack)) {
          event.preventDefault();
          event.stopImmediatePropagation();
          closeMobileCategory(categoryBack.closest(CATEGORY_PANEL_SELECTOR));
          return;
        }

        if (event.target.closest('[data-ks-menu-close]')) {
          event.preventDefault();
          event.stopImmediatePropagation();
          closeOuterMobileMenu(nav);
        }
      },
      true,
    );

    nav.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        handleEscape(nav);
        return;
      }

      const categoryTrigger = event.target.closest(
        CATEGORY_TRIGGER_SELECTOR,
      );
      if (categoryTrigger) {
        if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
          event.preventDefault();
          moveCategoryFocus(categoryTrigger, 1);
        } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
          event.preventDefault();
          moveCategoryFocus(categoryTrigger, -1);
        } else if (event.key === 'Home') {
          event.preventDefault();
          moveCategoryFocus(categoryTrigger, 'first');
        } else if (event.key === 'End') {
          event.preventDefault();
          moveCategoryFocus(categoryTrigger, 'last');
        }
      }
    });

  };

  const init = (scope = document) => {
    scope.querySelectorAll?.(NAV_SELECTOR).forEach(initNav);
  };

  document.addEventListener(
    'click',
    (event) => {
      const mobileToggle = event.target.closest('.nav-toggle');
      if (mobileToggle) {
        window.setTimeout(() => resetMobile(document), 0);
      }

      if (!DESKTOP.matches) return;
      if (event.target.closest(NAV_SELECTOR)) return;
      if (event.target.closest(`.${BACKDROP_CLASS}`)) return;
      closeDesktopMenus();
    },
    true,
  );

  document.addEventListener('focusin', (event) => {
    if (!DESKTOP.matches || !activeDesktopParent) return;
    if (event.target.closest(NAV_SELECTOR)) return;
    closeDesktopMenus();
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && DESKTOP.matches) closeDesktopMenus();
  });

  DESKTOP.addEventListener('change', () => {
    closeDesktopMenus();
    resetMobile(document);
  });

  HOVER_DESKTOP.addEventListener('change', cancelDesktopClose);

  window.addEventListener('scroll', requestPanelTopUpdate, {
    passive: true,
  });

  window.addEventListener('resize', requestPanelTopUpdate, {
    passive: true,
  });

  if (window.visualViewport) {
    window.visualViewport.addEventListener('resize', requestPanelTopUpdate, {
      passive: true,
    });
  }

  document.addEventListener('shopify:section:load', (event) => init(event.target));

  window[GLOBAL_KEY] = { init, closeDesktopMenus, resetMobile };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => init(), { once: true });
  } else {
    init();
  }
})();
